import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatISO } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import {
  subscribeToAppointments,
  subscribeToPendingAppointments,
  fetchRelatedData,
  enrichAppointments,
  fetchDropdownData,
  saveAppointment,
  softDeleteAppointment,
  updateAppointmentStatus,
  approveGuestAppointment,
  formatDateToday,
} from '../services/appointmentService';
import useAppointmentsStore from '../store/appointmentsStore';

export function useAppointments() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation('appointments');
  const navigate = useNavigate();

  // ── Server state (Firestore) ───────────────────────────────
  const [appointments,        setAppointments]        = useState([]);
  const [enrichedAppointments, setEnrichedAppointments] = useState([]);
  const [customers,           setCustomers]           = useState([]);
  const [services,            setServices]            = useState([]);
  const [employees,           setEmployees]           = useState([]);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [loading,             setLoading]             = useState(true);
  const [error,               setError]               = useState(null);
  const [fetchErrors,         setFetchErrors]         = useState([]);

  // ── Refs ──────────────────────────────────────────────────
  const unsubRef        = useRef(null);
  const pendingUnsubRef = useRef(null);

  // ── Store ─────────────────────────────────────────────────
  const viewedMonth      = useAppointmentsStore((s) => s.viewedMonth);
  const calendarViewMode = useAppointmentsStore((s) => s.calendarViewMode);

  // ── Helper: enrich + set ──────────────────────────────────
  const enrichAndSet = useCallback(
    (apptData, cust, svc, empl) => {
      const enriched = enrichAppointments(apptData, cust, svc, empl, t);
      setEnrichedAppointments(enriched);
    },
    [t],
  );

  // ── Main appointments subscription ────────────────────────
  useEffect(() => {
    if (authLoading || !user?.businessId) return;

    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }

    setLoading(true);
    setError(null);
    setFetchErrors([]);

    unsubRef.current = subscribeToAppointments({
      businessId:  user.businessId,
      targetDate:  viewedMonth,
      viewMode:    calendarViewMode,
      onData: async (apptData) => {
        setAppointments(apptData);
        try {
          const { customers: c, services: s, employees: e, errors } =
            await fetchRelatedData(apptData);
          setCustomers(c);
          setServices(s);
          setEmployees(e);
          if (errors.length) setFetchErrors(errors);
          enrichAndSet(apptData, c, s, e);
        } catch (err) {
          setFetchErrors([`${t('toast.relatedError')}: ${err.message}`]);
        }
        setLoading(false);
      },
      onError: (err) => {
        setError(`${t('toast.loadError')}: ${err.message}`);
        setLoading(false);
      },
    });

    return () => { if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; } };
  }, [user?.businessId, authLoading, viewedMonth, calendarViewMode, enrichAndSet, t]);

  // ── Pending appointments subscription ─────────────────────
  useEffect(() => {
    if (authLoading || !user?.businessId) return;
    if (pendingUnsubRef.current) { pendingUnsubRef.current(); }

    pendingUnsubRef.current = subscribeToPendingAppointments({
      businessId: user.businessId,
      onData: setPendingAppointments,
      onError: (err) => console.error('Pending randevu listener hatası:', err),
    });

    return () => { if (pendingUnsubRef.current) { pendingUnsubRef.current(); } };
  }, [user?.businessId, authLoading]);

  // ── enrichedAppointments'ı customers/services/employees değişince yenile ──
  useEffect(() => {
    if (appointments.length) enrichAndSet(appointments, customers, services, employees);
  }, [appointments, customers, services, employees, enrichAndSet]);

  // ── Helpers ───────────────────────────────────────────────
  const countByStatus = useCallback(
    (status) => enrichedAppointments.filter((a) => a.status === status).length,
    [enrichedAppointments],
  );

  const countToday = useCallback(() => {
    const today = formatDateToday();
    return enrichedAppointments.filter((a) => a.date === today && a.status !== 'deleted').length;
  }, [enrichedAppointments]);

  const formatDateForDisplay = useCallback(
    (dateString) => {
      if (!dateString) return '';
      try {
        const locale = t('locale') === 'en' ? 'en-GB' : 'tr-TR';
        return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric' })
          .format(new Date(dateString));
      } catch {
        return dateString;
      }
    },
    [t],
  );

  const getPendingDisplayName = useCallback(
    (appt) => {
      if (appt.source === 'guest') return appt.guestName || t('fallback.guest');
      return appt.customerName || appt.originalCustomerId || t('fallback.customer');
    },
    [t],
  );

  const getPendingServiceName = useCallback(
    (appt) => {
      if (appt.serviceDetails?.length) return appt.serviceDetails.map((s) => s.name).join(', ');
      if (appt.services?.length && typeof appt.services[0] === 'object')
        return appt.services.map((s) => s.name).join(', ');
      return t('fallback.service');
    },
    [t],
  );

  // ── handleOpenModal (yeni) ─────────────────────────────────
  const handleOpenModal = useCallback(async () => {
    const store = useAppointmentsStore.getState();
    try {
      const { customers: c, employees: e, services: s } = await fetchDropdownData(user.businessId);
      setCustomers(c);
      setEmployees(e);
      setServices(s);
    } catch (err) {
      toast.error(t('toast.dropdownError'));
    }
    store.openModal();
  }, [user?.businessId, t]);

  // ── handleEditAppointment ─────────────────────────────────
  const handleEditAppointment = useCallback(async (appointment) => {
    setLoading(true);
    try {
      const { customers: c, employees: e, services: s } = await fetchDropdownData(user.businessId);
      setCustomers(c);
      setEmployees(e);
      setServices(s);

      const selCustomer = c.find((x) => x.id === appointment.customerId);
      const selEmployee = e.find((x) => x.id === appointment.employeeId);

      const expandedServices = Array.isArray(appointment.services)
        ? appointment.services.flatMap((svc) =>
            Array.from({ length: Math.max(1, Math.floor(Number(svc.sessions) || 1)) }, () => ({
              id: svc.id, name: svc.name, price: svc.price,
            })),
          )
        : appointment.serviceId
          ? (() => {
              const det = s.find((x) => x.id === appointment.serviceId);
              return det ? [{ id: det.id, name: det.name, price: det.price }] : [];
            })()
          : [];

      useAppointmentsStore.getState().openEditModal({
        appointmentId: appointment.id,
        form: {
          date:             appointment.date,
          time:             appointment.time || '09:00',
          customerId:       appointment.customerId,
          employeeId:       appointment.employeeId,
          status:           appointment.status || 'pending',
          notes:            appointment.notes || '',
          paymentMethod:    appointment.paymentMethod || 'Nakit',
          selectedCustomer: selCustomer,
          selectedEmployee: selEmployee,
        },
        services: expandedServices,
      });
    } catch (err) {
      toast.error(t('toast.editError'));
    } finally {
      setLoading(false);
    }
  }, [user?.businessId, t]);

  // ── handleSaveAppointment ─────────────────────────────────
  const handleSaveAppointment = useCallback(async () => {
    const store = useAppointmentsStore.getState();
    const { appointmentForm, selectedServices, isEditing, currentAppointmentId } = store;

    if (
      !appointmentForm.date || !appointmentForm.time ||
      !appointmentForm.customerId || !appointmentForm.employeeId ||
      selectedServices.length === 0
    ) {
      toast.error(t('toast.requiredFields'));
      return;
    }

    store.setIsSaving(true);
    setLoading(true);
    try {
      await saveAppointment({
        appointmentData: { ...appointmentForm, services: selectedServices },
        isEditing,
        appointmentId: currentAppointmentId,
        userId: user.uid,
        businessId: user.businessId,
      });

      toast.success(isEditing ? t('toast.updated') : t('toast.created'));
      store.closeModal();
    } catch (err) {
      console.error('Randevu kayıt hatası:', err);
      toast.error(isEditing ? t('toast.updateError') : t('toast.saveError'));
    } finally {
      store.setIsSaving(false);
      setLoading(false);
    }
  }, [user, t]);

  // ── handleConfirmDelete ───────────────────────────────────
  const handleConfirmDelete = useCallback(async () => {
    const { appointmentToDelete, closeDeleteConfirm } = useAppointmentsStore.getState();
    if (!appointmentToDelete) return;
    setLoading(true);
    try {
      await softDeleteAppointment(appointmentToDelete.id);
      toast.success(t('toast.deleted'));
      closeDeleteConfirm();
    } catch (err) {
      toast.error(t('toast.deleteError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // ── handleApproveAppointment ──────────────────────────────
  const handleApproveAppointment = useCallback(async (appointment) => {
    useAppointmentsStore.getState().setActionLoading(appointment.id + '_approve');
    try {
      await approveGuestAppointment(appointment);
      toast.success(t('toast.approved'));
    } catch {
      toast.error(t('toast.approveError'));
    } finally {
      useAppointmentsStore.getState().setActionLoading(null);
    }
  }, [t]);

  // ── handleRejectAppointment ───────────────────────────────
  const handleRejectAppointment = useCallback(async (appointment) => {
    useAppointmentsStore.getState().setActionLoading(appointment.id + '_reject');
    try {
      await updateAppointmentStatus(appointment.id, 'canceled');
      toast.success(t('toast.rejected'));
    } catch {
      toast.error(t('toast.rejectError'));
    } finally {
      useAppointmentsStore.getState().setActionLoading(null);
    }
  }, [t]);

  // ── handleCompleteAppointment → bill onayı aç ─────────────
  const handleCompleteAppointment = useCallback((appointment) => {
    useAppointmentsStore.getState().openBillConfirm(appointment);
  }, []);

  // ── handleConfirmComplete ─────────────────────────────────
  const handleConfirmComplete = useCallback(async (createBill) => {
    const store = useAppointmentsStore.getState();
    const { appointmentToBill, closeBillConfirm, actionLoading } = store;
    if (!appointmentToBill) return;
    // Çift-tıklama koruması: devam eden bir işlem varsa yut
    if (actionLoading) return;

    // Durum guard'ı: yalnızca onaylanmış randevu tamamlanabilir
    if (appointmentToBill.status !== 'approved') {
      toast.warning(t('toast.completeInvalidStatus'));
      closeBillConfirm();
      return;
    }

    const previousStatus = appointmentToBill.status; // rollback için (approved)
    store.setActionLoading(appointmentToBill.id + '_complete');
    try {
      await updateAppointmentStatus(appointmentToBill.id, 'completed');
      toast.success(t('toast.completed'));

      if (createBill) {
        navigate('/home', {
          state: {
            createBillFromAppointment: true,
            appointmentData: {
              customerId:    appointmentToBill.customerId,
              employeeId:    appointmentToBill.employeeId,
              services:      appointmentToBill.services || [],
              notes:         appointmentToBill.notes || '',
              paymentMethod: appointmentToBill.paymentMethod || 'Nakit',
            },
            // Adisyon kaydedilmezse randevuyu eski durumuna geri döndürmek için
            appointmentCompletion: {
              appointmentId:  appointmentToBill.id,
              previousStatus,
            },
          },
        });
      }
    } catch (err) {
      console.error('[useAppointments] complete error:', err);
      toast.error(t('toast.completeError'));
    } finally {
      closeBillConfirm();
      useAppointmentsStore.getState().setActionLoading(null);
    }
  }, [t, navigate]);

  // ── handleDaySelect ───────────────────────────────────────
  const handleDaySelect = useCallback((date) => {
    const appts = enrichedAppointments.filter((a) => a.date === date && a.status !== 'deleted');
    useAppointmentsStore.getState().setSelectedDate(date);
    useAppointmentsStore.getState().setSelectedAppointments(appts);
  }, [enrichedAppointments]);

  // ── handleRefresh ─────────────────────────────────────────
  const handleRefresh = useCallback(() => {
    setLoading(true);
    setFetchErrors([]);
    const store = useAppointmentsStore.getState();
    // viewedMonth değişikliği useEffect'i tetikler; burada sadece state'i sıfırla
    store.setViewedMonth(new Date(store.viewedMonth));
  }, []);

  return {
    // Server state
    enrichedAppointments,
    pendingAppointments,
    customers,
    employees,
    services,
    loading,
    error,
    fetchErrors,
    // Stats
    countByStatus,
    countToday,
    // Display helpers
    formatDateForDisplay,
    getPendingDisplayName,
    getPendingServiceName,
    // Handlers
    handleOpenModal,
    handleEditAppointment,
    handleSaveAppointment,
    handleConfirmDelete,
    handleApproveAppointment,
    handleRejectAppointment,
    handleCompleteAppointment,
    handleConfirmComplete,
    handleDaySelect,
    handleRefresh,
  };
}
